import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Database, ShoppingCart, Loader2, X, Check, AlertTriangle, RefreshCw, Car, Cpu, CornerDownLeft, ChevronDown, ChevronUp, LayoutGrid, Rows3, SlidersHorizontal, Zap, ShieldCheck, FileDown, Info, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import SafeHtml from './SafeHtml';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Customer-facing Original Files Database — searchable catalog of stock
 * ECU files with cascading Brand → Model → Engine selectors plus six
 * free-text fields. Buying triggers a confirmation modal with three
 * checkboxes (configurable in admin settings) and creates an order with
 * vehicle_type="Original files" that the operator fulfils manually.
 */
export default function OriginalFilesPage({ token, isAdmin = false, onChargedWallet, onNavigate }) {
  const { t } = useTranslation('original-files');
  const { t: tCommon, i18n } = useTranslation('common');
  const tNav = (k) => tCommon(`nav.${k}`);
  const [settings, setSettings] = useState(null);
  // Admin-editable layout labels/placeholders/hints (block titles,
  // field captions, button text). Fetched alongside settings; falls
  // back to in-component defaults if the endpoint hiccups so the page
  // never renders with empty headings.
  const [layout, setLayout] = useState({
    page_title: 'Original Files Database',
    page_subtitle: 'Search across 193k+ ECU dumps — synced live with Alientech catalog',
    stat_total_label: 'total files', stat_added_label: 'added today', stat_sync_label: 'last sync',
    block1_title: 'Quick search', block1_badge: 'Most used',
    block1_hint: 'Type HW, SW, calibration or part of name. Multi-word works.',
    block1_placeholder: 'Search by HW, SW, calibration, part of name…',
    block2_title: 'Vehicle', block2_hint: 'Narrow by car brand, model and engine',
    label_brand: 'Brand', label_model: 'Model', label_engine: 'Engine',
    placeholder_brand: 'All brands', placeholder_model: 'All models', placeholder_engine: 'All engines',
    block3_title: 'ECU control unit', block3_hint: 'Filter by ECU manufacturer and exact part numbers',
    label_ecu_brand: 'ECU brand', label_ecu_model: 'ECU model',
    label_hw: 'HW number', label_sw: 'SW number', label_sw_upgrade: 'SW upgrade',
    label_calibration: 'Calibration', label_damos: 'Damos',
    placeholder_ecu_brand: 'All ECU brands', placeholder_ecu_model: 'Pick ECU brand first',
    placeholder_hw: 'e.g. 0281010710', placeholder_sw: 'e.g. 0000036091D1401',
    placeholder_sw_upgrade: 'e.g. 000034D61D1803', placeholder_calibration: 'e.g. 00002862626086A',
    placeholder_damos: 'e.g. S013184',
    btn_search: 'Search', btn_reset: 'Reset all', btn_buy: 'Buy',
    result_count_singular: 'match', result_count_plural: 'matches',
    empty_state: 'No files match your filters. Try widening the search.',
  });
  const [filters, setFilters]   = useState({ brands: [], models: [], engines: [], ecu_brands: [], ecu_models: [] });
  const [stats,   setStats]     = useState(null);

  // Search inputs
  const [brand,    setBrand]    = useState('');
  const [model,    setModel]    = useState('');
  const [engine,   setEngine]   = useState('');
  const [ecuBrand, setEcuBrand] = useState('');
  const [ecuModel, setEcuModel] = useState('');
  const [hw,       setHw]       = useState('');
  const [sw,       setSw]       = useState('');
  const [swUpg,    setSwUpg]    = useState('');
  const [calib,    setCalib]    = useState('');
  const [damos,    setDamos]    = useState('');
  // Free-text search across HW/SW/calibration/damos/brand/model/engine/etc.
  // Backend `q` param maps to a $regex across every visible column so
  // a single string like "B58" or "0281010710" finds matches no matter
  // which column the value lives in.
  const [q,        setQ]        = useState('');

  // Results / paging
  const [results, setResults] = useState([]);
  const [total, setTotal]     = useState(0);
  const [totalCapped, setTotalCapped] = useState(false);
  const [searched, setSearched] = useState(false);
  const [searching, setSearching] = useState(false);
  const [skip, setSkip]       = useState(0);
  const PAGE = 50;

  // Presentation state. Once the customer has searched, the page turns
  // from "landing" into "work surface": the intro collapses, the long
  // filter blocks fold away and the quick-search bar sticks to the top,
  // so results start right under it. A logged-in customer is one click
  // from buying — they do not need the marketing copy again.
  const [heroOpen, setHeroOpen] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [view, setView] = useState('cards');      // 'cards' | 'table'
  const [viewLocked, setViewLocked] = useState(false); // user picked manually
  const [expanded, setExpanded] = useState(null);  // row id with open details
  // Step history for the Back button: every new search pushes the state
  // it replaces, so a cross-link ("all files for this ECU") or a refined
  // query can be undone instead of retyped.
  const [history, setHistory] = useState([]);
  const canGoBack = history.length > 0;

  // Buy modal
  const [buyTarget, setBuyTarget] = useState(null);
  const [accept1, setAccept1] = useState(false);
  const [accept2, setAccept2] = useState(false);
  const [accept3, setAccept3] = useState(false);
  const [accept4, setAccept4] = useState(false);
  const [purchasing, setPurchasing] = useState(false);

  const headers = useMemo(() => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }), [token]);

  // Load settings + cascade root + catalog stats + admin-editable labels once.
  // Each request is independent: a slow/failed filters or stats call
  // must never keep the whole page on "Loading…".
  const [loadError, setLoadError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 30000);
    const get = (url, opts = {}) => fetch(url, { ...opts, signal: ctrl.signal })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))));
    setLoadError('');
    get(`${API_URL}/api/original-files/settings/public`, { headers })
      .then(setSettings)
      .catch((e) => setLoadError(e.name === 'AbortError' ? 'Server did not respond in 30s' : e.message));
    get(`${API_URL}/api/original-files/filters`, { headers }).then(setFilters).catch(() => {});
    get(`${API_URL}/api/original-files/stats`, { headers }).then(setStats).catch(() => {});
    get(`${API_URL}/api/config/original-files-layout`)
      .then((lay) => { if (lay && typeof lay === 'object') setLayout((prev) => ({ ...prev, ...lay })); })
      .catch(() => {});
    return () => { clearTimeout(timer); ctrl.abort(); };
  }, [headers, reloadKey]);

  // Context-aware faceting — every selector excludes options that have
  // no matching row in the database given the OTHER selected facets.
  // Picking BMW prunes the ECU brand dropdown to only those used by
  // BMW; picking BOSCH back-prunes the Brand list to those that have
  // BOSCH ECUs. Single endpoint call after every change keeps the
  // dropdowns in sync without a chain of `if (brand) { ... if (model) }`
  // effects (the old code was ~30 lines that only narrowed brand→model→engine).
  useEffect(() => {
    let cancelled = false;
    const p = new URLSearchParams();
    if (brand)    p.set('brand', brand);
    if (model)    p.set('model', model);
    if (engine)   p.set('engine', engine);
    if (ecuBrand) p.set('ecu_brand', ecuBrand);
    if (ecuModel) p.set('ecu_model', ecuModel);
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/original-files/filters?${p.toString()}`, { headers });
        if (!r.ok) return;
        const f = await r.json();
        if (cancelled) return;
        setFilters((prev) => ({ ...prev, ...f }));
      } catch (_) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [brand, model, engine, ecuBrand, ecuModel, headers]);

  const buildQs = useCallback((extraSkip = 0) => {
    const p = new URLSearchParams();
    if (brand)    p.set('brand', brand);
    if (model)    p.set('model', model);
    if (engine)   p.set('engine', engine);
    if (ecuBrand) p.set('ecu_brand', ecuBrand);
    if (ecuModel) p.set('ecu_model', ecuModel);
    if (hw)       p.set('hw', hw);
    if (sw)       p.set('sw', sw);
    if (swUpg)    p.set('sw_upgrade', swUpg);
    if (calib)    p.set('calibration', calib);
    if (damos)    p.set('damos', damos);
    if (q && q.trim()) p.set('q', q.trim());
    p.set('limit', String(PAGE));
    p.set('skip', String(extraSkip));
    return p.toString();
  }, [brand, model, engine, ecuBrand, ecuModel, hw, sw, swUpg, calib, damos, q]);

  const doSearch = async (append = false, override = null) => {
    if (!append) {
      const snap = {
        q, brand, model, engine, ecuBrand, ecuModel, hw, sw, swUpg, calib, damos,
        results, total, totalCapped, skip, searched, view, heroOpen, filtersOpen,
      };
      // Only remember steps that actually showed something.
      if (snap.searched) setHistory((h) => [...h.slice(-9), snap]);
    }
    setSearching(true);
    try {
      const qs = override
        ? new URLSearchParams({ ...override, limit: String(PAGE), skip: '0' }).toString()
        : buildQs(append ? skip : 0);
      const r = await fetch(`${API_URL}/api/original-files/search?${qs}`, { headers });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setResults(append ? [...results, ...j.items] : j.items);
      setTotal(j.total || 0);
      setTotalCapped(!!j.total_capped);
      setSkip((append ? skip : 0) + (j.items?.length || 0));
      setSearched(true);
      if (!append) {
        // Switch to work mode and pick the denser layout when the result
        // set is long — unless the customer already chose a view.
        setHeroOpen(false);
        setFiltersOpen(false);
        setExpanded(null);
        if (!viewLocked) setView((j.total || 0) > 20 ? 'table' : 'cards');
      }
    } catch (e) {
      toast.error('Search failed', { description: e.message });
    } finally { setSearching(false); }
  };

  /** "All files for this ECU / this model" — the public site has these
   *  cross-links, and they save a buyer who found no exact match from
   *  retyping a whole search. Filters are replaced, not merged: the point
   *  is to widen, so leftover HW/SW numbers must go. */
  const searchSameEcu = (r) => {
    setQ(''); setHw(''); setSw(''); setSwUpg(''); setCalib(''); setDamos('');
    setBrand(''); setModel(''); setEngine('');
    setEcuBrand(r.ecu_brand || ''); setEcuModel(r.ecu_model || '');
    const o = {};
    if (r.ecu_brand) o.ecu_brand = r.ecu_brand;
    if (r.ecu_model) o.ecu_model = r.ecu_model;
    doSearch(false, o);
  };
  const searchSameModel = (r) => {
    setQ(''); setHw(''); setSw(''); setSwUpg(''); setCalib(''); setDamos('');
    setEcuBrand(''); setEcuModel(''); setEngine('');
    setBrand(r.brand || ''); setModel(r.model || '');
    const o = {};
    if (r.brand) o.brand = r.brand;
    if (r.model) o.model = r.model;
    doSearch(false, o);
  };

  const goBack = () => {
    const prev = history[history.length - 1];
    if (!prev) return;
    setHistory((h) => h.slice(0, -1));
    setQ(prev.q); setBrand(prev.brand); setModel(prev.model); setEngine(prev.engine);
    setEcuBrand(prev.ecuBrand); setEcuModel(prev.ecuModel);
    setHw(prev.hw); setSw(prev.sw); setSwUpg(prev.swUpg); setCalib(prev.calib); setDamos(prev.damos);
    setResults(prev.results); setTotal(prev.total); setTotalCapped(prev.totalCapped);
    setSkip(prev.skip); setSearched(prev.searched); setView(prev.view);
    setHeroOpen(prev.heroOpen); setFiltersOpen(prev.filtersOpen);
    setExpanded(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const reset = () => {
    setBrand(''); setModel(''); setEngine(''); setEcuBrand(''); setEcuModel('');
    setHw(''); setSw(''); setSwUpg(''); setCalib(''); setDamos(''); setQ('');
    setResults([]); setTotal(0); setTotalCapped(false); setSearched(false); setSkip(0);
    setHeroOpen(true); setFiltersOpen(true); setExpanded(null); setHistory([]);
  };

  const openBuy = (file) => { setBuyTarget(file); setAccept1(false); setAccept2(false); setAccept3(false); setAccept4(false); };
  const closeBuy = () => { if (!purchasing) setBuyTarget(null); };
  const confirmBuy = async () => {
    if (!buyTarget) return;
    setPurchasing(true);
    try {
      const r = await fetch(`${API_URL}/api/original-files/purchase`, {
        method: 'POST', headers, body: JSON.stringify({ file_id: buyTarget.id }),
      });
      const j = await r.json();
      if (!r.ok) {
        toast.error('Purchase failed', { description: j.detail || `HTTP ${r.status}` });
        return;
      }
      toast.success(`Order #${j.order_number} created`, {
        description: j.success_text || 'Your original file will be attached as soon as the operator processes it.',
        duration: 8000,
      });
      setBuyTarget(null);
      if (onChargedWallet) onChargedWallet();
    } catch (e) {
      toast.error('Purchase failed', { description: e.message });
    } finally { setPurchasing(false); }
  };

  if (!settings && loadError) {
    return (
      <div className="p-8 text-center text-slate-600" data-testid="original-files-load-error">
        <div className="mb-3">Could not load the original files database ({loadError}).</div>
        <button onClick={() => setReloadKey(k => k + 1)} className="px-4 py-2 bg-slate-900 text-white rounded-lg text-sm" data-testid="original-files-retry">Retry</button>
      </div>
    );
  }
  if (!settings) {
    return <div className="p-8 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />{tCommon('status.loading')}</div>;
  }
  if (!settings.enabled) {
    return <div className="p-8 text-center text-slate-500">{t('store_disabled')}</div>;
  }

  const c = settings.column_labels || {};
  const activeFilterCount = [brand, model, engine, ecuBrand, ecuModel, hw, sw, swUpg, calib, damos].filter(Boolean).length;

  return (
    <div className="p-4 md:p-6 w-full lg:w-[70%] mx-auto space-y-4 sm:space-y-6" data-testid="original-files-page">
      {/* Hero / intro — full on arrival, one line once the customer works
          with results (they have already read it on the public site). */}
      {heroOpen ? (
      <div className="bg-gradient-to-br from-blue-600 via-cyan-600 to-teal-600 text-white rounded-2xl shadow-xl p-4 sm:p-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center flex-shrink-0">
            <Database className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight">{t('title')}</h1>
            <SafeHtml as="div" className="mt-1.5 text-white/90 text-xs sm:text-sm leading-snug [&_a]:underline [&_b]:font-semibold"
              data-testid="orig-intro" html={t('info_banner', { defaultValue: settings.intro_text || '' })} />
          </div>
          {searched && (
            <button onClick={() => setHeroOpen(false)} data-testid="orig-hero-collapse"
              className="flex-shrink-0 p-1.5 rounded-lg bg-white/15 hover:bg-white/25" title="Collapse">
              <ChevronUp className="w-4 h-4" />
            </button>
          )}
        </div>
        {(() => {
          // Localisation policy for the disclaimer banner:
          //  • On English, prefer admin-managed `settings.attention_text` so
          //    operators can keep customising it.
          //  • On any other customer language, prefer the localised string
          //    shipped in `original-files:warning_banner` so non-EN users
          //    don't see English; admin's attention_text is used only as a
          //    last-resort fallback if the locale is missing the key.
          const lang = (i18n.language || 'en').toLowerCase();
          const isEnglish = lang === 'en' || lang.startsWith('en-');
          const adminText = (settings.attention_text || '').trim();
          const localised = t('warning_banner', { defaultValue: '' });
          const html = isEnglish
            ? (adminText || localised)
            : (localised || adminText);
          if (!html) return null;
          return (
            <SafeHtml as="div" className="mt-3 rounded-lg bg-rose-500/30 border border-rose-200/40 p-3 text-xs sm:text-sm text-white [&_b]:font-bold"
              data-testid="orig-attention" html={html} />
          );
        })()}

        {/* Catalog size badges — shown only when stats are available so
            an empty preview environment doesn't render placeholders. */}
        {stats && (
          <div className="mt-4 flex flex-wrap items-center gap-2 sm:gap-3" data-testid="orig-stats">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/20 backdrop-blur text-white text-xs sm:text-sm font-bold border border-white/30">
              <Database className="w-3.5 h-3.5" />
              <span className="tabular-nums" data-testid="orig-stats-total">{Number(stats.total || 0).toLocaleString()}</span>
              <span className="font-normal opacity-90">{t('files_in_catalog')}</span>
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-400/30 backdrop-blur text-white text-xs sm:text-sm font-bold border border-emerald-200/40">
              <span className="tabular-nums" data-testid="orig-stats-month">+{Number(stats.added_last_month || 0).toLocaleString()}</span>
              <span className="font-normal opacity-90">{t('added_this_month')}</span>
            </span>
          </div>
        )}
      </div>
      ) : (
        <button onClick={() => setHeroOpen(true)} data-testid="orig-hero-strip"
          className="w-full flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-teal-600 text-white text-xs sm:text-sm font-bold shadow">
          <Database className="w-4 h-4" />
          <span className="truncate">{t('title')}</span>
          {stats && (
            <span className="ml-auto flex items-center gap-2 font-normal opacity-90 whitespace-nowrap">
              <span className="tabular-nums">{Number(stats.total || 0).toLocaleString()}</span>
              <span className="hidden sm:inline">{t('files_in_catalog')}</span>
              <ChevronDown className="w-4 h-4" />
            </span>
          )}
        </button>
      )}

      {/* ─── 3-BLOCK SEARCH FORM ─────────────────────────────────
          Block 1 — primary free-text search (most customers use only
          this). Block 2 — Vehicle cascade. Block 3 — ECU info with both
          dropdowns and exact part numbers. Labels/placeholders are
          admin-editable through Settings → Original files page. */}
      <section className="space-y-4" data-testid="orig-search-form">

        {/* BLOCK 1 — Quick search. Sticky once there are results, so the
            customer can refine a number without scrolling back up. */}
        <div className={`bg-white border border-slate-200 rounded-2xl shadow-sm p-4 sm:p-6 ${
          searched ? 'sticky top-0 z-30 shadow-md' : ''}`}>
          <div className="flex items-center gap-2.5 mb-3 flex-wrap">
            <span className="w-8 h-8 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 text-white font-extrabold text-sm inline-flex items-center justify-center">1</span>
            <h3 className="text-base font-extrabold text-slate-800">{t('search.title', { defaultValue: layout.block1_title })}</h3>
            {layout.block1_badge && (
              <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-[10px] font-bold uppercase tracking-wider" data-testid="orig-b1-badge">{t('most_used', { defaultValue: layout.block1_badge })}</span>
            )}
            <span className="text-xs text-slate-400 hidden sm:inline ml-auto">{t('search.hint_b1', { defaultValue: layout.block1_hint })}</span>
          </div>
          {/* Quick search: a part number is ~10 characters — a full-width
              field just looks empty. Narrow input + the primary action
              right next to it. */}
          <div className="flex flex-wrap items-stretch gap-2.5">
            <div className="flex items-center gap-3 bg-emerald-50 border-2 border-emerald-200 focus-within:border-emerald-500 focus-within:bg-white focus-within:ring-4 focus-within:ring-emerald-100 rounded-xl px-4 py-3 transition-all w-full sm:w-[38%] sm:min-w-[300px]">
              <Search className="w-5 h-5 text-emerald-600 flex-shrink-0" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') doSearch(false); }}
                placeholder={(() => {
                  const lang = (i18n.language || 'en').toLowerCase();
                  if (lang === 'en' || lang.startsWith('en-')) return layout.block1_placeholder;
                  return t('search_placeholder_main', { defaultValue: layout.block1_placeholder });
                })()}
                data-testid="orig-input-q"
                className="flex-1 min-w-0 bg-transparent outline-none text-base font-semibold text-slate-800 placeholder:text-emerald-500/60 placeholder:font-normal"
              />
              <span className="hidden sm:inline-flex text-[10px] font-bold tracking-wider text-emerald-700 bg-emerald-100 px-2 py-1 rounded font-mono items-center gap-1">
                <CornerDownLeft className="w-3 h-3" /> ENTER
              </span>
            </div>

            <button
              onClick={() => doSearch(false)} disabled={searching}
              className="px-6 py-3 bg-slate-900 hover:bg-slate-800 active:scale-[0.98] text-white rounded-xl text-sm font-extrabold tracking-wide flex items-center justify-center gap-2 shadow-lg shadow-slate-900/15 transition-all disabled:opacity-50 flex-1 sm:flex-none"
              data-testid="orig-search-btn"
            >
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {t('search.search_button', { defaultValue: layout.btn_search })}
            </button>

            {canGoBack && (
              <button
                onClick={goBack} disabled={searching}
                className="px-4 py-3 bg-white border-2 border-slate-300 hover:border-slate-400 hover:bg-slate-50 active:scale-[0.98] text-slate-700 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all"
                data-testid="orig-back-btn"
              >
                <ArrowLeft className="w-4 h-4" />
                {t('back', { defaultValue: 'Back' })}
              </button>
            )}
          </div>

          {/* Action row — always reachable, even with the filter blocks folded */}
          <div className="mt-3 flex items-center gap-2.5 flex-wrap">
            <button
              onClick={() => setFiltersOpen((v) => !v)}
              data-testid="orig-filters-toggle"
              className="px-4 py-2.5 border-2 border-slate-200 hover:border-cyan-300 hover:bg-cyan-50/50 rounded-xl text-sm font-bold text-slate-700 flex items-center gap-2 transition-all"
            >
              <SlidersHorizontal className="w-4 h-4 text-cyan-600" />
              {t('search.vehicle_section', { defaultValue: 'Filters' })}
              {!!activeFilterCount && (
                <span className="px-1.5 rounded-md bg-cyan-600 text-white text-xs tabular-nums">{activeFilterCount}</span>
              )}
              {filtersOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>

            <span className="text-sm text-slate-500" data-testid="orig-result-count">
              {searched ? (
                <>
                  <b className="text-slate-900 text-base">{totalCapped ? `${total.toLocaleString()}+` : total.toLocaleString()}</b>{' '}
                  {total === 1 ? t('result_count_singular', { defaultValue: layout.result_count_singular }) : t('result_count_plural', { defaultValue: layout.result_count_plural })}
                  {totalCapped && <span className="ml-1 text-[11px] text-slate-400">{t('narrow_filters_hint')}</span>}
                </>
              ) : (
                <span className="text-slate-400 text-xs">{t('search.placeholders.search_any')}</span>
              )}
            </span>

            <span className="flex-1" />

            {searched && results.length > 0 && (
              <div className="hidden md:inline-flex bg-slate-100 rounded-xl p-1" data-testid="orig-view-switch">
                {[{ id: 'cards', icon: LayoutGrid }, { id: 'table', icon: Rows3 }].map((v) => (
                  <button key={v.id} onClick={() => { setView(v.id); setViewLocked(true); }}
                    data-testid={`orig-view-${v.id}`} title={v.id}
                    className={`px-3 py-1.5 rounded-lg transition-all ${view === v.id ? 'bg-white shadow text-slate-900' : 'text-slate-400 hover:text-slate-600'}`}>
                    <v.icon className="w-4 h-4" />
                  </button>
                ))}
              </div>
            )}
            <button
              onClick={reset} disabled={searching}
              className="px-4 py-2.5 border-2 border-slate-200 hover:border-rose-300 hover:bg-rose-50/50 rounded-xl text-sm font-bold text-slate-600 transition-all"
              data-testid="orig-reset-btn"
            >{t('search.reset_button', { defaultValue: layout.btn_reset })}</button>
          </div>
        </div>

        {filtersOpen && (
        <>
        {/* BLOCK 2 — Vehicle */}
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-4 sm:p-6">
          <div className="flex items-center gap-2.5 mb-3 flex-wrap">
            <span className="w-8 h-8 rounded-xl bg-gradient-to-br from-cyan-500 to-cyan-700 text-white font-extrabold text-sm inline-flex items-center justify-center"><Car className="w-4 h-4" /></span>
            <h3 className="text-base font-extrabold text-slate-800">{t('search.vehicle_section', { defaultValue: layout.block2_title })}</h3>
            <span className="text-xs text-slate-400 hidden sm:inline ml-auto">{t('search.hint_b2', { defaultValue: layout.block2_hint })}</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-4xl">
            <LabeledSelector label={t('search.labels.manufacturer', { defaultValue: layout.label_brand })}  value={brand}  onChange={(v) => { setBrand(v); setModel(''); setEngine(''); }} options={filters.brands}  placeholder={t('select_brand', { defaultValue: layout.placeholder_brand })}  testid="orig-sel-brand" accent="cyan" />
            <LabeledSelector label={t('search.labels.model', { defaultValue: layout.label_model })}  value={model}  onChange={(v) => { setModel(v); setEngine(''); }} options={filters.models} disabled={!brand} placeholder={t('select_model', { defaultValue: layout.placeholder_model })}  testid="orig-sel-model" accent="cyan" />
            <LabeledSelector label={t('search.labels.engine', { defaultValue: layout.label_engine })} value={engine} onChange={setEngine} options={filters.engines} disabled={!model} placeholder={t('select_engine', { defaultValue: layout.placeholder_engine })} testid="orig-sel-engine" accent="cyan" />
          </div>
        </div>

        {/* BLOCK 3 — ECU control unit */}
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-4 sm:p-6">
          <div className="flex items-center gap-2.5 mb-3 flex-wrap">
            <span className="w-8 h-8 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 text-white font-extrabold text-sm inline-flex items-center justify-center"><Cpu className="w-4 h-4" /></span>
            <h3 className="text-base font-extrabold text-slate-800">{t('search.ecu_section', { defaultValue: layout.block3_title })}</h3>
            <span className="text-xs text-slate-400 hidden sm:inline ml-auto">{t('search.hint_b3', { defaultValue: layout.block3_hint })}</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mb-4">
            <LabeledSelector label={t('search.labels.ecu_brand', { defaultValue: layout.label_ecu_brand })} value={ecuBrand} onChange={setEcuBrand} options={filters.ecu_brands} placeholder={t('select_ecu_brand', { defaultValue: layout.placeholder_ecu_brand })} testid="orig-sel-ecu-brand" accent="orange" />
            <LabeledSelector label={t('search.labels.ecu_type', { defaultValue: layout.label_ecu_model })} value={ecuModel} onChange={setEcuModel} options={filters.ecu_models} disabled={!ecuBrand} placeholder={t('select_ecu_model', { defaultValue: layout.placeholder_ecu_model })} testid="orig-sel-ecu-model" accent="orange" />
          </div>
          <div className={`grid grid-cols-2 ${isAdmin ? 'sm:grid-cols-5' : 'sm:grid-cols-4'} gap-4 max-w-4xl`}>
            <NumberAutocomplete label={t('search.labels.hardware', { defaultValue: layout.label_hw })}          field="hw_number"   value={hw}    onChange={setHw}    placeholder={t('search.placeholders.hw_number', { defaultValue: layout.placeholder_hw })}          testid="orig-input-hw"          headers={headers} scope={{ brand, model, engine, ecu_brand: ecuBrand, ecu_model: ecuModel }} />
            <NumberAutocomplete label={t('search.labels.software', { defaultValue: layout.label_sw })}          field="sw_number"   value={sw}    onChange={setSw}    placeholder={t('search.placeholders.sw_number', { defaultValue: layout.placeholder_sw })}          testid="orig-input-sw"          headers={headers} scope={{ brand, model, engine, ecu_brand: ecuBrand, ecu_model: ecuModel }} />
            <NumberAutocomplete label={t('sw_upgrade', { defaultValue: layout.label_sw_upgrade })}  field="sw_upgrade"  value={swUpg} onChange={setSwUpg} placeholder={t('sw_upgrade_ph', { defaultValue: layout.placeholder_sw_upgrade })}  testid="orig-input-sw-upgrade"  headers={headers} scope={{ brand, model, engine, ecu_brand: ecuBrand, ecu_model: ecuModel }} />
            <NumberAutocomplete label={t('calibration', { defaultValue: layout.label_calibration })} field="calibration" value={calib} onChange={setCalib} placeholder={t('calibration_ph', { defaultValue: layout.placeholder_calibration })} testid="orig-input-calibration" headers={headers} scope={{ brand, model, engine, ecu_brand: ecuBrand, ecu_model: ecuModel }} />
            {isAdmin && (
              <NumberInput label={layout.label_damos} value={damos} onChange={setDamos} placeholder={layout.placeholder_damos} testid="orig-input-damos" />
            )}
          </div>
        </div>
        </>
        )}
      </section>

      {/* Results */}
      {searched && (
        <section className="space-y-2.5" data-testid="orig-results">
          {results.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl p-6 text-sm text-slate-500 text-center">{t('results.no_results')}</div>
          ) : (
            <>
              {/* Compare-your-read hint — the one thing that actually
                  prevents a wrong purchase. Replaces the long marketing
                  copy from the public site. */}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-1 text-[11px] text-slate-500">
                <span className="inline-flex items-center gap-1 font-semibold text-slate-600">
                  <Info className="w-3.5 h-3.5 text-cyan-600" />
                  {t('compare_hint', { defaultValue: 'Compare HW / SW with your own read — the identifiers decide, not the vehicle name.' })}
                </span>
                <span className="inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-emerald-600" /> Alientech / KESS3</span>
                <span className="inline-flex items-center gap-1"><Zap className="w-3 h-3 text-amber-500" /> {t('instant_access', { defaultValue: 'Instant access' })}</span>
                <span className="inline-flex items-center gap-1"><FileDown className="w-3 h-3 text-slate-400" /> {t('unmodified', { defaultValue: 'Original, unmodified' })}</span>
                <span className="inline-flex items-center gap-1 text-amber-700 font-semibold" data-testid="orig-no-refund-badge">
                  <AlertTriangle className="w-3 h-3" /> {t('no_refunds_short', { defaultValue: 'No refunds — verify HW/SW first' })}
                </span>

              </div>

              {/* ── Card list (default, mirrors the public site) ── */}
              <div className={view === 'table' ? 'md:hidden space-y-2.5' : 'space-y-2.5'} data-testid="orig-results-cards">
                {results.map((r) => (
                  <FileCard key={r.id} r={r} t={t} c={c}
                    open={expanded === r.id}
                    onToggle={() => setExpanded(expanded === r.id ? null : r.id)}
                    onSameEcu={() => searchSameEcu(r)}
                    onSameModel={() => searchSameModel(r)}
                    onBuy={() => openBuy(r)} />
                ))}
              </div>

              {/* ── Dense table (desktop only, for long result sets) ── */}
              {view === 'table' && (
                <div className="hidden md:block bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="orig-results-table">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider">
                      <tr>
                        <th className="text-left px-3 py-2">{c.brand_model}</th>
                        <th className="text-left px-3 py-2">{c.ecu_model}</th>
                        <th className="text-left px-3 py-2">{c.hw_number} / {c.sw_number}</th>
                        <th className="text-right px-3 py-2">{c.price}</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {results.map((r) => (
                        <React.Fragment key={`t-${r.id}`}>
                          <tr className="border-t border-slate-100 hover:bg-slate-50/60 cursor-pointer"
                            onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                            data-testid={`orig-row-${r.id}`}>
                            <td className="px-3 py-2">
                              <div className="font-semibold text-slate-800">{r.brand} {r.model}</div>
                              <div className="text-[11px] text-slate-500">{r.engine || '—'}</div>
                            </td>
                            <td className="px-3 py-2 text-slate-700 whitespace-nowrap">
                              <span className="font-semibold">{r.ecu_brand}</span> {r.ecu_model}
                            </td>
                            <td className="px-3 py-2 font-mono text-[11px] text-slate-600 whitespace-nowrap">
                              <div>HW {r.hw_number || '—'}</div>
                              <div>SW {r.sw_number || '—'}</div>
                            </td>
                            <td className="px-3 py-2 text-right font-extrabold text-slate-900 tabular-nums whitespace-nowrap">
                              €{Number(r.price_eur || 0).toFixed(0)}
                            </td>
                            <td className="px-3 py-2 text-right whitespace-nowrap">
                              <button onClick={(e) => { e.stopPropagation(); openBuy(r); }}
                                className="px-4 py-2 bg-rose-500 hover:bg-rose-600 text-white rounded-xl text-sm font-extrabold shadow-md shadow-rose-500/20 transition-all"
                                data-testid={`orig-buy-${r.id}`}>{t('buy')}</button>
                              <span className="ml-1.5 inline-block text-slate-300">
                                {expanded === r.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                              </span>
                            </td>
                          </tr>
                          {expanded === r.id && (
                            <tr className="bg-slate-50/70" data-testid={`orig-details-${r.id}`}>
                              <td colSpan={5} className="px-3 py-3">
                                <SpecGrid r={r} c={c} />
                                <RelatedLinks r={r} t={t}
                                  onSameEcu={() => searchSameEcu(r)}
                                  onSameModel={() => searchSameModel(r)} />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Load more */}
              {(results.length < total || totalCapped) && (
                <div className="pt-2 text-center">
                  <button
                    onClick={() => doSearch(true)} disabled={searching}
                    className="px-6 py-3 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-sm font-extrabold flex items-center gap-2 mx-auto shadow-lg shadow-slate-900/15 transition-all disabled:opacity-50"
                    data-testid="orig-load-more"
                  >{searching && <RefreshCw className="w-4 h-4 animate-spin" />} {t('load_more')} ({results.length} / {totalCapped ? `${total.toLocaleString()}+` : total})</button>
                </div>
              )}

              {/* Delivery footnote — answers the two questions support gets
                  most after a purchase: what is in the download, and why
                  the extension is .ori. */}
              {(layout.notice_delivery_zip || layout.notice_extension) && (
                <div className="mt-4 rounded-xl border border-slate-200 bg-white p-3.5" data-testid="orig-delivery-note">
                  <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 mb-1.5">
                    {t('how_delivered', { defaultValue: 'How you receive the file' })}
                  </p>
                  <ul className="space-y-1.5">
                    {layout.notice_delivery_zip && (
                      <li className="flex items-start gap-2 text-xs text-slate-600">
                        <FileDown className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
                        <span>{t('notice_delivery_zip', { defaultValue: layout.notice_delivery_zip })}</span>
                      </li>
                    )}
                    {layout.notice_extension && (
                      <li className="flex items-start gap-2 text-xs text-slate-600">
                        <Info className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
                        <span>
                          {t('notice_extension', { defaultValue: layout.notice_extension })}
                        </span>
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </>
          )}
        </section>
      )}

      {/* Buy modal */}
      {buyTarget && (() => {
        // Localisation policy: prefer admin-managed buy_modal text in EN
        // (so operators can keep customising); on non-EN locales prefer
        // shipped translations (purchase_modal.*) so customers see their
        // language. Same `pickLocalisedCopy` pattern as the attention
        // banner and EVCDashboard.
        const lang = (i18n.language || 'en').toLowerCase();
        const isEnglish = lang === 'en' || lang.startsWith('en-');
        const m = settings.buy_modal || {};
        const pick = (adminVal, key, fallback) => {
          const admin = (adminVal || '').trim ? (adminVal || '').trim() : (adminVal || '');
          const tr = t(`purchase_modal.${key}`, { defaultValue: '' });
          if (isEnglish) return admin || tr || fallback;
          return tr || admin || fallback;
        };
        const titleHtml    = pick(m.title,        'title',        'Confirmation of purchase original file');
        const introHtml    = pick(m.intro,        'intro',        '');
        const cb1Html      = pick(m.checkbox_1,   'checkbox_1',   '');
        const cb2Html      = pick(m.checkbox_2,   'checkbox_2',   '');
        const cb3Html      = pick(m.checkbox_3,   'checkbox_3',   '');
        const cb4Html      = pick(m.checkbox_4,   'checkbox_4',   '');
        const buyLabel     = pick(m.buy_label,    'buy_label',    'Buy file');
        const cancelLabel  = pick(m.cancel_label, 'cancel_label', 'Cancel');
        return (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={closeBuy}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="orig-buy-modal">
            {/* Header: what exactly is being bought + price, nothing else */}
            <div className="p-5 border-b border-slate-100 flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <h3 className="font-bold text-slate-900 text-base">{buyTarget.brand} {buyTarget.model}</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  {buyTarget.engine ? `${buyTarget.engine} · ` : ''}
                  <span className="font-semibold">{buyTarget.ecu_brand}</span> {buyTarget.ecu_model}
                </p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <Chip label="HW" value={buyTarget.hw_number} />
                  <Chip label="SW" value={buyTarget.sw_number} />
                  <Chip label="UPG" value={buyTarget.sw_upgrade} />
                  <Chip label="CAL" value={buyTarget.calibration} />
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-2xl font-extrabold text-slate-900 tabular-nums leading-none">€{Number(buyTarget.price_eur || 0).toFixed(2)}</div>
                <div className="text-[10px] text-slate-400 uppercase tracking-wider mt-1">{t('one_time', { defaultValue: 'one-time' })}</div>
              </div>
              <button onClick={closeBuy} className="text-slate-300 hover:text-slate-600 flex-shrink-0"><X className="w-5 h-5" /></button>
            </div>

            <div className="p-5 space-y-4">
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-500">
                <span className="inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-emerald-600" /> Alientech / KESS3</span>
                <span className="inline-flex items-center gap-1"><Zap className="w-3 h-3 text-amber-500" /> {t('instant_access', { defaultValue: 'Instant access' })}</span>
                <span className="inline-flex items-center gap-1"><FileDown className="w-3 h-3 text-slate-400" /> {t('unmodified', { defaultValue: 'Original, unmodified' })}</span>
              </div>

              <h4 className="text-sm font-bold text-slate-800">{titleHtml}</h4>
              {introHtml && (
                <SafeHtml as="div" className="rounded-lg bg-rose-50 border border-rose-200 p-3 text-xs text-slate-700 [&_b]:font-bold" html={introHtml} />
              )}

              {/* Before you buy — the three facts that cause refund
                  requests when nobody reads them. Amber, compact, right
                  above the consent boxes. */}
              {(layout.notice_no_refund || layout.notice_file_size || layout.notice_formats) && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3" data-testid="orig-buy-notices">
                  <p className="text-[10px] font-extrabold uppercase tracking-wider text-amber-700 mb-1.5 flex items-center gap-1">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    {t('before_you_buy', { defaultValue: 'Before you buy' })}
                  </p>
                  <ul className="space-y-1.5 text-xs text-amber-900">
                    {layout.notice_no_refund && (
                      <li className="flex gap-1.5"><span className="text-amber-500">•</span>
                        <span className="font-semibold">{t('notice_no_refund', { defaultValue: layout.notice_no_refund })}</span></li>
                    )}
                    {layout.notice_file_size && (
                      <li className="flex gap-1.5"><span className="text-amber-500">•</span>
                        <span>{t('notice_file_size', { defaultValue: layout.notice_file_size })}</span></li>
                    )}
                    {layout.notice_formats && (
                      <li className="flex gap-1.5"><span className="text-amber-500">•</span>
                        <span>{t('notice_formats', { defaultValue: layout.notice_formats })}</span></li>
                    )}
                  </ul>
                </div>
              )}
              <div className="space-y-2.5">
                <CheckboxRow checked={accept1} onChange={setAccept1} testid="orig-modal-cb1" html={cb1Html} />
                <CheckboxRow checked={accept2} onChange={setAccept2} testid="orig-modal-cb2" html={cb2Html} />
                <CheckboxRow checked={accept3} onChange={setAccept3} testid="orig-modal-cb3" html={cb3Html} />
                {cb4Html && (
                  <CheckboxRow checked={accept4} onChange={setAccept4} testid="orig-modal-cb4" html={cb4Html} />
                )}
              </div>
            </div>

            <div className="p-5 pt-0 flex items-center gap-2">
              <button onClick={closeBuy} disabled={purchasing}
                className="px-4 py-2.5 border border-slate-300 hover:bg-slate-50 rounded-lg text-sm font-medium"
                data-testid="orig-modal-cancel"
              >{cancelLabel}</button>
              <button
                onClick={confirmBuy}
                disabled={!accept1 || !accept2 || !accept3 || (cb4Html && !accept4) || purchasing}
                className="flex-1 px-4 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="orig-modal-buy"
              >
                {purchasing ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingCart className="w-4 h-4" />}
                {buyLabel} — €{Number(buyTarget.price_eur || 0).toFixed(2)}
              </button>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
}

function Chip({ label, value, mono = true }) {
  if (!value) return null;
  return (
    <span className="inline-flex items-baseline gap-1.5 px-2.5 py-1.5 rounded-lg bg-slate-50 border border-slate-200 text-xs max-w-full">
      <b className="text-slate-500 font-bold uppercase tracking-wide text-[9px]">{label}</b>
      <span className={`${mono ? 'font-mono' : ''} text-slate-800 break-all`}>{value}</span>
    </span>
  );
}

/** Full specification grid — the "File specifications" table from the
 *  public site, minus everything a logged-in buyer does not need. */
function SpecGrid({ r, c }) {
  const rows = [
    [c.engine || 'Engine', r.engine],
    [c.ecu_model || 'ECU', [r.ecu_brand, r.ecu_model].filter(Boolean).join(' ')],
    [c.hw_number || 'Hardware', r.hw_number],
    [c.sw_number || 'Software', r.sw_number],
    [c.sw_upgrade || 'SW upgrade', r.sw_upgrade],
    [c.calibration || 'Calibration', r.calibration],
    ['DAMOS', r.damos],
    ['Engine code', r.engine_code],
  ].filter(([, v]) => v);
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
      {rows.map(([k, v]) => (
        <div key={k} className="flex items-baseline gap-2 text-xs border-b border-dashed border-slate-200 pb-1">
          <dt className="text-slate-500 w-28 flex-shrink-0">{k}</dt>
          <dd className="font-mono text-slate-800 break-all">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

/** One search hit — the layout customers already know from the public
 *  site: vehicle big, ECU under it, identifiers as chips, price + Buy on
 *  the right. Details (calibration, SW upgrade, DAMOS) unfold in place,
 *  so nobody leaves the page one click before buying. */
/** Cross-links to neighbouring files — the way out when the exact
 *  identifiers do not match. */
function RelatedLinks({ r, t, onSameEcu, onSameModel }) {
  return (
    <div className="mt-2.5 flex flex-wrap gap-2">
      {(r.ecu_brand || r.ecu_model) && (
        <button onClick={onSameEcu} data-testid={`orig-same-ecu-${r.id}`}
          className="px-3.5 py-2 rounded-xl border-2 border-cyan-200 bg-cyan-50 hover:bg-cyan-100 hover:border-cyan-300 text-xs font-bold text-cyan-800 inline-flex items-center gap-1.5 max-w-full transition-all">
          <Cpu className="w-3 h-3 flex-shrink-0" />
          <span className="truncate">
            {t('all_for_ecu', { defaultValue: 'All files for' })} {r.ecu_brand} {r.ecu_model}
          </span>
        </button>
      )}
      {(r.brand || r.model) && (
        <button onClick={onSameModel} data-testid={`orig-same-model-${r.id}`}
          className="px-3.5 py-2 rounded-xl border-2 border-slate-200 bg-slate-50 hover:bg-slate-100 hover:border-slate-300 text-xs font-bold text-slate-700 inline-flex items-center gap-1.5 max-w-full transition-all">
          <Car className="w-3 h-3 flex-shrink-0" />
          <span className="truncate">
            {t('all_for_model', { defaultValue: 'All files for' })} {r.brand} {r.model}
          </span>
        </button>
      )}
    </div>
  );
}

function FileCard({ r, t, c, open, onToggle, onBuy, onSameEcu, onSameModel }) {
  return (
    <div className="bg-white border border-slate-200 hover:border-slate-300 rounded-xl shadow-sm transition-colors"
      data-testid={`orig-card-${r.id}`}>
      <div className="p-3 flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <h3 className="text-[15px] font-bold text-slate-900 leading-tight">{r.brand} {r.model}</h3>
            {r.engine && <span className="text-xs text-slate-500">{r.engine}</span>}
            <span className="text-xs text-slate-600">
              <span className="font-bold text-slate-700">{r.ecu_brand}</span>{r.ecu_model ? ` ${r.ecu_model}` : ''}
            </span>
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <Chip label="HW" value={r.hw_number} />
            <Chip label="SW" value={r.sw_number} />
            <Chip label="UPG" value={r.sw_upgrade} />
            <Chip label="CAL" value={r.calibration} />
            <button onClick={onToggle} data-testid={`orig-details-toggle-${r.id}`}
              className="px-2 py-1 rounded-lg text-xs font-bold text-slate-500 hover:text-slate-800 hover:bg-slate-100 inline-flex items-center gap-1 transition-colors">
              {t('details', { defaultValue: 'Details' })}
              {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="text-lg font-extrabold text-slate-900 tabular-nums leading-none">
            €{Number(r.price_eur || 0).toFixed(0)}
          </div>
          <button onClick={onBuy} data-testid={`orig-buy-card-${r.id}`}
            className="px-5 py-2.5 bg-rose-500 hover:bg-rose-600 active:scale-[0.98] text-white rounded-xl text-sm font-extrabold shadow-lg shadow-rose-500/25 transition-all">
            {t('buy')}
          </button>
        </div>
      </div>

      {open && (
        <div className="px-3 pb-3 pt-1 border-t border-slate-100" data-testid={`orig-card-details-${r.id}`}>
          <SpecGrid r={r} c={c} />
          <RelatedLinks r={r} t={t} onSameEcu={onSameEcu} onSameModel={onSameModel} />
        </div>
      )}
    </div>
  );
}

function Selector({ label, value, onChange, options = [], disabled, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-extrabold uppercase tracking-wider text-slate-600">{label}</span>
      <select
        value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}
        className="mt-1 w-full border border-slate-300 rounded-lg px-2.5 py-2 text-sm bg-white disabled:bg-slate-50 disabled:text-slate-400"
        data-testid={testid}
      >
        <option value="">{disabled ? 'Select previous first…' : `Any ${label.toLowerCase()}`}</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
function Input({ label, value, onChange, placeholder, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-extrabold uppercase tracking-wider text-slate-600">{label}</span>
      <input
        type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="mt-1 w-full border border-slate-300 rounded-lg px-2.5 py-2 text-sm font-mono"
        data-testid={testid}
      />
    </label>
  );
}

/**
 * AutocompleteInput — same look as <Input> but with a dropdown of DB
 * suggestions fired off after ≥ 6 chars. Used for HW/SW/SW upgrade
 * /Calibration where the values are long opaque strings the customer
 * is unlikely to remember exactly. Backend `GET /original-files/suggest`
 * scopes its candidates by the currently-selected brand/model/engine
 * /ECU so noise stays low.
 */
function AutocompleteInput({ label, value, onChange, placeholder, testid, field, headers, scope, minLen = 6 }) {
  const [open, setOpen] = React.useState(false);
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const reqId = React.useRef(0);
  const debounceTimer = React.useRef(null);

  // Re-run suggest on value change, but throttled so each keystroke
  // doesn't hammer the backend.
  React.useEffect(() => {
    if (!value || value.length < minLen) { setItems([]); setOpen(false); return; }
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    const my = ++reqId.current;
    debounceTimer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const p = new URLSearchParams({ field, q: value });
        Object.entries(scope || {}).forEach(([k, v]) => { if (v) p.set(k, v); });
        const r = await fetch(`${API_URL}/api/original-files/suggest?${p.toString()}`, { headers });
        if (!r.ok || my !== reqId.current) return;
        const j = await r.json();
        // Hide the dropdown if the only match equals the current value
        // (user typed it in full) — avoids the visual noise.
        const vs = (j.values || []).filter((v) => v && v !== value);
        setItems(vs);
        setOpen(vs.length > 0);
      } catch (_) { /* silent */ }
      finally { setLoading(false); }
    }, 220);
    return () => { if (debounceTimer.current) clearTimeout(debounceTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, field]);

  return (
    <label className="block relative">
      <span className="text-xs font-extrabold uppercase tracking-wider text-slate-600">{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => { if (items.length) setOpen(true); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
        className="mt-1 w-full border border-slate-300 rounded-lg px-2.5 py-2 text-sm font-mono"
        data-testid={testid}
        autoComplete="off"
      />
      {open && items.length > 0 && (
        <div className="absolute z-30 left-0 right-0 top-full mt-1 max-h-60 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg" data-testid={`${testid}-dropdown`}>
          {loading && <div className="px-3 py-2 text-xs text-slate-400">…</div>}
          {items.slice(0, 15).map((v) => (
            <button
              key={v}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); onChange(v); setOpen(false); }}
              className="block w-full text-left px-3 py-1.5 text-sm font-mono hover:bg-cyan-50"
              data-testid={`${testid}-opt-${v}`}
            >{v}</button>
          ))}
        </div>
      )}
    </label>
  );
}
function CheckboxRow({ checked, onChange, html, testid }) {
  return (
    <label className="flex items-start gap-2 cursor-pointer text-sm text-slate-700" data-testid={testid}>
      <span className={`mt-0.5 w-4 h-4 rounded border-2 flex-shrink-0 flex items-center justify-center transition ${checked ? 'bg-emerald-500 border-emerald-500' : 'bg-white border-slate-300'}`}>
        {checked && <Check className="w-3 h-3 text-white" />}
      </span>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="sr-only" />
      <SafeHtml as="span" className="[&_b]:font-bold leading-snug" html={html || ''} />
    </label>
  );
}


/**
 * LabeledSelector — fancier wrapper around the old `Selector`. Renders
 * an uppercase mini-label above a styled <select>. When a value is
 * picked, the field gets a colored border (cyan for Vehicle, orange for
 * ECU) so the customer sees at a glance which fields are filtered.
 */
function LabeledSelector({ label, value, onChange, options = [], disabled, placeholder, testid, accent = 'cyan' }) {
  const accentRing = accent === 'orange' ? 'focus:ring-orange-400 focus:border-orange-500' : 'focus:ring-cyan-400 focus:border-cyan-500';
  const accentFilled = accent === 'orange' ? 'border-orange-400 bg-orange-50 text-orange-800' : 'border-cyan-400 bg-cyan-50 text-cyan-800';
  return (
    <label className="block">
      <span className="text-xs font-extrabold uppercase tracking-wider text-slate-600">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={`mt-1.5 w-full border-2 rounded-xl px-3.5 py-3 text-[15px] font-semibold outline-none transition-all bg-white text-slate-800 shadow-sm hover:border-slate-300 ${accentRing} focus:ring-4 disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed ${value ? accentFilled : 'border-slate-200'}`}
        data-testid={testid}
      >
        <option value="">{placeholder || 'All'}{options.length ? ` · ${options.length} available` : ''}</option>
        {options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
      </select>
    </label>
  );
}

/**
 * NumberAutocomplete — orange-themed restyling of <AutocompleteInput>.
 * Behaviour identical (≥6-char debounce, scoped suggestions); only the
 * visuals follow the Block 3 accent.
 */
function NumberAutocomplete({ label, value, onChange, placeholder, testid, field, headers, scope, minLen = 6 }) {
  const [open, setOpen] = React.useState(false);
  const [items, setItems] = React.useState([]);
  const reqId = React.useRef(0);
  const debounceTimer = React.useRef(null);

  React.useEffect(() => {
    if (!value || value.length < minLen) { setItems([]); setOpen(false); return; }
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    const my = ++reqId.current;
    debounceTimer.current = setTimeout(async () => {
      try {
        const p = new URLSearchParams({ field, q: value });
        Object.entries(scope || {}).forEach(([k, v]) => { if (v) p.set(k, v); });
        const r = await fetch(`${API_URL}/api/original-files/suggest?${p.toString()}`, { headers });
        if (!r.ok || my !== reqId.current) return;
        const j = await r.json();
        const vs = (j.values || []).filter((v) => v && v !== value);
        setItems(vs);
        setOpen(vs.length > 0);
      } catch (_) { /* silent */ }
    }, 220);
    return () => { if (debounceTimer.current) clearTimeout(debounceTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, field]);

  return (
    <label className="block relative">
      <span className="text-xs font-extrabold uppercase tracking-wider text-slate-600">{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => { if (items.length) setOpen(true); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
        className={`mt-1.5 w-full border-2 rounded-xl px-3.5 py-3 text-[15px] font-mono font-semibold outline-none transition-all shadow-sm hover:border-slate-300 focus:ring-4 focus:ring-orange-100 ${value ? 'border-orange-400 bg-orange-50/40 text-orange-900' : 'border-slate-200 bg-white'} focus:border-orange-500`}
        data-testid={testid}
        autoComplete="off"
      />
      {open && items.length > 0 && (
        <div className="absolute z-30 left-0 right-0 top-full mt-1 max-h-60 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg" data-testid={`${testid}-dropdown`}>
          {items.map((s, i) => (
            <button
              key={`${s}-${i}`}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onChange(s); setOpen(false); }}
              className="block w-full text-left px-3.5 py-2.5 text-sm font-mono font-semibold hover:bg-orange-50"
              data-testid={`${testid}-option-${i}`}
            >{s}</button>
          ))}
        </div>
      )}
    </label>
  );
}

/** NumberInput — orange-themed wrapper for the Damos field (admin only). */
function NumberInput({ label, value, onChange, placeholder, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-extrabold uppercase tracking-wider text-slate-600">{label}</span>
      <input
        type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className={`mt-1.5 w-full border-2 rounded-xl px-3.5 py-3 text-[15px] font-mono font-semibold outline-none transition-all shadow-sm hover:border-slate-300 focus:ring-4 focus:ring-orange-100 ${value ? 'border-orange-400 bg-orange-50/40 text-orange-900' : 'border-slate-200 bg-white'} focus:border-orange-500`}
        data-testid={testid}
      />
    </label>
  );
}
